/**
 * pdf.js — the deal report as a PDF
 * =================================
 * Brian, 30 September 2026: "lets add a feature where i can hit a button and
 * it produces a PDF of the deal report i am looking at".
 *
 * The server makes it, from the payload this page holds for the deal
 * (reports/services/deal_report_pdf.py): the header, the quick stats, the
 * warnings, the curve with its tranche lines, the figures, funding rounds and
 * every cash movement on the curve. So the paper and the screen cannot
 * disagree about a figure, and the file is a real PDF with its text in it
 * rather than a picture of the page — which is why a deal's page uses this
 * rather than the browser's print-to-PDF the list page offers.
 */
import React, { useEffect, useState } from "react";
import api from "../../api/client";
import theme from "../../components/theme";

export const DEAL_PDF_ENDPOINT = "/reports/deal-cash-series/";

// The server names the file after the deal and the day; the fallback only
// has to be a name, never a claim.
export function pdfFilename(disposition, dealId) {
  const match = (disposition || "").match(/filename="?([^";]+)"?/);
  return (match && match[1]) || `Helena_Deal_Report_${dealId}.pdf`;
}

// `Blob.text()` where the browser has it; FileReader, which every browser
// and the test DOM have, where it does not.
const blobText = (blob) =>
  typeof blob.text === "function"
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

// What went wrong, in the server's words. A request made for a file gets its
// error body back as a file too, so it has to be read before it can be shown.
export async function pdfError(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await blobText(data));
      if (body?.error) return body.error;
    } catch (e) {
      // Not JSON: say what the request said instead.
    }
  }
  return `${err?.response?.status || ""} ${err?.message || "the PDF could not be made"}`.trim();
}

export async function downloadDealPdf(dealId) {
  const response = await api.get(DEAL_PDF_ENDPOINT, {
    params: { deal: dealId, format: "pdf" },
    responseType: "blob",
  });
  const name = pdfFilename(response.headers?.["content-disposition"], dealId);
  const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
  return name;
}

export function DealPdfButton({ dealId }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Another deal is another report: an error about the last one must not
  // sit under the next one's button.
  useEffect(() => setError(""), [dealId]);

  const onClick = async () => {
    setBusy(true);
    setError("");
    try {
      await downloadDealPdf(dealId);
    } catch (err) {
      setError(await pdfError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      <button
        type="button"
        className="pt-button no-print"
        onClick={onClick}
        disabled={busy}
        title="This deal's report as a PDF: the figures, the curve, funding rounds and every cash movement"
      >
        <span aria-hidden="true">⎙</span> {busy ? "Preparing PDF…" : "Download PDF"}
      </button>
      {error && (
        <span role="alert" style={{ fontSize: 11, color: theme.danger, maxWidth: 280 }}>
          PDF not made: {error}
        </span>
      )}
    </span>
  );
}
